import { useMaterialReactTable } from 'material-react-table';
import { formatDateString } from 'src/utils/date';
import { beautifyEnum, formatNumber, SimpleFormatNumber } from 'src/utils/utility';
import EyeIcon from 'src/assets/images/icons/eye.svg';
import { CO2, Kg, M3, Ton } from 'src/presentations/components/atom/unit';
import StatusChip from 'src/presentations/components/organisms/dashboard/StatusChip';
import { CustomTooltip } from 'src/presentations/components/atom/customtooltip';
import React, { ReactNode } from 'react';
import { shipmentTypeIcons } from '../../../molecules/table/NewInvoiceTable.tsx';
import { InvoiceAnalyticsDto } from '../../../../../generated/services/TFinancialApi.ts';

export const useAnalyticsTable = (tableOptions) =>
  useMaterialReactTable({
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableRowSelection: false,
    enableColumnActions: false,
    enablePagination: false, // Disable table pagination - using FilterNav pagination
    enableTopToolbar: false,
    enableBottomToolbar: false, // Disable bottom toolbar - using FilterNav pagination
    enableSortingRemoval: false,
    enableEditing: true,
    editDisplayMode: 'table',
    muiTableHeadCellProps: {
      align: 'center',
    },
    muiTableBodyCellProps: {
      align: 'center',
    },
    ...tableOptions,
  });

export const getShipmentsColumns = ({ dateReference, currentCurrency }: { dateReference: string; currentCurrency?: string }) => {
  dateColumnOptions.find((option) => option.value === dateReference);

  const normalizeShipmentStatusReason = (reason: string) =>
    reason
      .replace(/spot shipment/gi, '')
      .replace(/\bspot\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
  const buildShipmentStatusReasons = (reasons: string[]) =>
    reasons.map((reason) => ({
      text: normalizeShipmentStatusReason(reason),
      hasSpot: /spot shipment|spot/i.test(reason),
    }));

  return [
    {
      enableEditing: false,
      enableSorting: false,
      accessorFn: (row) => shipmentTypeIcons[row.shipmentType],
      accessorKey: 'aggregateShipmentType',
      header: ' ',
      size: 90,
      Cell: ({ cell, row }) => {
        const shipmentTypeDisplay = (() => {
          switch (row.original.shipmentType?.toLowerCase()) {
            case 'import':
              return 'INBOUND';
            case 'export':
              return 'OUTBOUND';
            default:
              return row.original.shipmentType || 'MULTI';
          }
        })();

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <CustomTooltip title={shipmentTypeDisplay}>
              <div>{(cell.getValue() as ReactNode) || null}</div>
            </CustomTooltip>
          </div>
        );
      },
    },
    {
      enableEditing: false,
      accessorKey: 'shipmentNumber',
      accessorFn: (row) => row.shipmentNumber || '-',
      header: 'SHIP NO.',
      size: 20,
      Cell: ({ row }) => (
        <div className='flex items-center justify-center gap-8'>
          <span>{row.original.shipmentNumber || '-'}</span>
          {row.original.isSpotShipment && <span className='text-[10px] py-2 px-4 font-semibold rounded-6 bg-neutral-10 text-neutral-50'>SPOT</span>}
        </div>
      ),
    },
    {
      enableEditing: false,
      accessorKey: 'mbl',
      accessorFn: (row) => row.mbl || '-',
      header: 'MBL/MAWB',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'serviceType',
      accessorFn: (row) => row.serviceType || '-',
      header: 'SERVICE',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'date',
      accessorFn: (row) => {
        let date: string;
        switch (dateReference) {
          case 'PICKUP_DATE':
            date = row.pickupDate;
            break;
          case 'DELIVERY_DATE':
            date = row.deliveryDate;
            break;
          case 'INVOICE_DATE':
            date = row.invoiceDate;
            break;
          case 'INVOICE_DUE_DATE':
            date = row.invoiceDueDate;
            break;
          case 'SUBMISSION_DATE':
            date = row.submissionDate;
            break;
          default:
            throw new Error('Invalid date reference');
        }
        return formatDateString(date);
      },
      header: 'DATE',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>DATE</div>
          <div className='text-10 xl:text-12 text-[#808080] font-medium'>{beautifyEnum(dateReference)}</div>
        </div>
      ),
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'leadTime',
      accessorFn: (row) => row.leadTime,
      header: 'LEAD TIME',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'invoiceNumber',
      accessorFn: (row) => row.invoiceNumber || '-',
      header: 'INVOICE NO.',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'serviceProvider',
      accessorFn: (row) => beautifyEnum(row.serviceProvider) || '-',
      header: 'SERVICE PROVIDER',
      size: 150,
    },
    {
      enableEditing: false,
      accessorKey: 'modality',
      accessorFn: (row) => row.modality || '-',
      header: 'MODALITY',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'billedEntity',
      accessorFn: (row) => (row.billedEntity ? beautifyEnum(row.billedEntity).toUpperCase() : '-'),
      header: 'BILLED ENTITY',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'invoiceAmountAfterTax',
      accessorFn: (row) => `${row?.invoiceCurrency || '-'} ${formatNumber(row?.invoiceAmountAfterTax) || '-'}`,
      header: 'Shipment Amount (Invoice Currency)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>SHIP. AMOUNT</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Invoice Currency</span>
        </div>
      ),
      size: 180,
      minSize: 180,
    },
    {
      enableEditing: false,
      accessorKey: 'totalAmount',
      accessorFn: (row) => {
        const currency = row?.totalAmountCurrency || currentCurrency;
        return `${currency || '-'} ${formatNumber(row?.totalAmount) || '-'}`;
      },
      header: 'Shipment Amount (Currency Reference)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>SHIP. AMOUNT</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Currency Reference</span>
        </div>
      ),
      size: 180,
      minSize: 180,
    },
    {
      enableEditing: false,
      accessorKey: 'totalChargeAmountLeg1',
      accessorFn: (row) => {
        const currency = row?.invoiceCurrency;
        const amount = row?.totalChargeAmountLeg1;
        if (!currency || amount == null) {
          return '-';
        }
        return `${currency} ${formatNumber(amount)}`;
      },
      header: 'Total Charge Amount (Leg 1 - Invoice Currency)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>TOTAL CHARGE AMOUNT</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Leg 1 - Invoice Currency</span>
        </div>
      ),
      size: 250,
      minSize: 250,
    },
    {
      enableEditing: false,
      accessorKey: 'totalChargeAmountLeg2',
      accessorFn: (row) => {
        const currency = row?.invoiceCurrency;
        const amount = row?.totalChargeAmountLeg2;
        if (!currency || amount == null) {
          return '-';
        }
        return `${currency} ${formatNumber(amount)}`;
      },
      header: 'Total Charge Amount (Leg 2 - Invoice Currency)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>TOTAL CHARGE AMOUNT</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Leg 2 - Invoice Currency</span>
        </div>
      ),
      size: 250,
      minSize: 250,
    },
    {
      enableEditing: false,
      accessorKey: 'totalChargeAmountLeg3',
      accessorFn: (row) => {
        const currency = row?.invoiceCurrency;
        const amount = row?.totalChargeAmountLeg3;

        if (!currency || amount == null) {
          return '-';
        }
        return `${currency} ${formatNumber(amount)}`;
      },
      header: 'Total Charge Amount (Leg 3 - Invoice Currency)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>TOTAL CHARGE AMOUNT</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Leg 3 - Invoice Currency</span>
        </div>
      ),
      size: 250,
      minSize: 250,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'shipmentChargesCount',
      accessorFn: (row) => row.shipmentChargesCount || '-',
      header: 'CHARGES COUNT',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'shipmentStatus',
      accessorFn: (row) => row.shipmentStatus || '-',
      header: 'SHIP. STATUS',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'shipmentStatusReason',
      accessorFn: (row) => {
        const reasons = row.shipmentStatusReason || [];
        return reasons.length > 0 ? reasons.join(', ') : '-';
      },
      header: 'REASON FOR SHIP. STATUS',
      size: 220,
      muiTableBodyCellProps: {
        align: 'center',
      },
      Cell: ({ row }) => {
        const reasons = row.original.shipmentStatusReason || [];
        if (reasons.length === 0) return <span>-</span>;

        const formattedReasons = buildShipmentStatusReasons(reasons);

        return (
          <div className='flex flex-col items-center'>
            {formattedReasons.map((item, index) => (
              <div key={`${item.text || 'spot'}-${index}`} className='flex items-center gap-8'>
                {item.text ? <span>{item.text}</span> : null}
                {item.hasSpot && <span className='text-[10px] py-2 px-4 font-semibold rounded-6 bg-neutral-10 text-neutral-50'>SPOT</span>}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      enableEditing: false,
      accessorKey: 'pol',
      accessorFn: (row) => row.pol || '-',
      header: 'POL',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'pod',
      accessorFn: (row) => row.pod || '-',
      header: 'POD ',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'grossWeight',
      accessorFn: (row) => (
        <span>
          {SimpleFormatNumber(row.grossWeight) || '-'} <Kg />
        </span>
      ),
      header: 'GROSS WEIGHT',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'chargeableWeight',
      accessorFn: (row) => (
        <span>
          {SimpleFormatNumber(row.chargeableWeight) || '-'} <Kg />
        </span>
      ),
      header: 'CHARGEABLE WEIGHT ',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'volume',
      accessorFn: (row) => (
        <span>
          {row.volume || '-'} <M3 />
        </span>
      ),
      header: 'VOLUME',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'shipper',
      accessorFn: (row) => beautifyEnum(row.shipper)?.toUpperCase() || '-',
      header: 'SHIPPER NAME',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'shipperCountry',
      accessorFn: (row) => row.shipperCountry || '-',
      header: 'SHIPPER COUNTRY',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'consignee',
      accessorFn: (row) => (row.consignee ? beautifyEnum(row.consignee).toUpperCase() : '-'),
      header: 'CONSIGNEE NAME',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'consigneeCountry',
      accessorFn: (row) => row.consigneeCountry || '-',
      header: 'CONSIGNEE COUNTRY',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'goodsName',
      accessorFn: (row) => row.goodsName || '-',
      header: 'GOODS NAME',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'co2Emission',
      accessorFn: (row) => (
        <span>
          {row.co2Emission} <Ton />
        </span>
      ),
      header: 'CO2 Emission',
      Header: (
        <>
          <CO2 /> EMISSION
        </>
      ),
      size: 150,
    },
    {
      enableEditing: false,
      accessorKey: 'vehicleId',
      accessorFn: (row) => row.vehicleId || '-',
      header: 'VEHICLE ID',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'shipmentCurrentDeviationWorkflow',
      accessorFn: (row) => {
        const value = row.shipmentCurrentDeviationWorkflow;
        if (Array.isArray(value)) {
          const formatted = value
            .map((item) => beautifyEnum(item))
            .filter(Boolean)
            .join(', ');
          return formatted || '-';
        }
        return beautifyEnum(value) || '-';
      },
      header: 'REVIEW STAGE',
      size: 20,
    },
    {
      enableEditing: false,
      accessorFn: (row) => (
        <button type='button' onClick={() => window.open(`/action-details/${row.invoiceId}?hideBack=1&shipmentNumber=${row.shipmentNumber}`, '_blank')}>
          <img src={EyeIcon} alt='Detail' />
        </button>
      ),
      header: 'DETAILS',
      accessorKey: 'actions',
      enableSorting: false,
      size: 20,
    },
  ].filter(Boolean); // Filter out null/falsy columns
};

export const getInvoicesColumns = ({ dateReference, currentCurrency }: { dateReference: string; currentCurrency?: string }) =>
  [
    {
      enableEditing: false,
      enableSorting: false,
      accessorFn: (row) => shipmentTypeIcons[row.shipmentType],
      accessorKey: 'aggregateShipmentType',
      header: ' ',
      size: 90,
      Cell: ({ cell, row }) => {
        // Map shipmentType to the desired display text
        const shipmentTypeDisplay = (() => {
          switch (row.original.shipmentType?.toLowerCase()) {
            case 'import':
              return 'INBOUND';
            case 'export':
              return 'OUTBOUND';
            default:
              return row.original.shipmentType || 'MULTI';
          }
        })();

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <CustomTooltip title={shipmentTypeDisplay}>
              <div>{(cell.getValue() as ReactNode) || null}</div>
            </CustomTooltip>
          </div>
        );
      },
    },
    {
      enableEditing: false,
      accessorKey: 'invoiceNumber',
      accessorFn: (row) => row.invoiceNumber || '-',
      header: 'INVOICE NO.',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'invoiceStatus',
      accessorFn: (row) => <StatusChip status={row.invoiceStatus} />,
      header: 'STATUS ',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'reason',
      accessorFn: (row) => {
        const reasons = row.reasons || [];
        return reasons.length > 0 ? reasons.join(', ') : '-';
      },
      header: 'REASON',
      size: 220,
      muiTableBodyCellProps: {
        align: 'center',
      },
      Cell: ({ row }) => {
        const reasons = row.original.reasons || [];
        if (reasons.length === 0) return <span>-</span>;

        const hasSpotShipment = reasons.some((reason) => reason.toLowerCase().includes('spot shipment'));
        const filteredReasons = hasSpotShipment ? reasons.filter((reason) => !reason.toLowerCase().includes('spot shipment')) : reasons;

        return (
          <div className='flex flex-col items-center'>
            {filteredReasons.map((reason, index) => (
              <div key={index} className='flex items-center gap-8'>
                <span>{reason}</span>
                {index === 0 && hasSpotShipment && <span className='text-[10px] py-2 px-4 font-semibold rounded-6 bg-neutral-10 text-neutral-50'>SPOT</span>}
              </div>
            ))}
            {hasSpotShipment && filteredReasons.length === 0 && (
              <div className='flex items-center gap-8'>
                <span className='text-[10px] py-2 px-4 font-semibold rounded-6 bg-neutral-10 text-neutral-50'>SPOT</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      enableEditing: false,
      accessorKey: 'shipmentNumber',
      accessorFn: (row: InvoiceAnalyticsDto) => {
        const rowData = row as any;
        // Check if shipmentCount exists and is greater than 1
        if (rowData?.shipmentCount && rowData?.shipmentCount > 1) {
          return `${rowData?.shipmentCount} Shipments`;
        }
        // Otherwise show shipmentNumber if it exists
        return row?.shipmentNumber || '-';
      },
      header: 'SHIP NO.',
      size: 20,
      Cell: ({ row }) => {
        const shipmentCount = (row.original as any).shipmentCount || 0;
        const { shipmentNumber } = row.original;
        const { hasSpotShipment } = row.original; // Invoices use hasSpotShipment

        let displayText = '-';
        // Show multiple shipments text only if count is greater than 1
        if (shipmentCount > 1) {
          displayText = `${shipmentCount} Shipments`;
        } else if (shipmentNumber) {
          // Show shipmentNumber if it exists (regardless of shipmentCount)
          displayText = shipmentNumber;
        }

        return (
          <div className='flex items-center justify-center gap-8'>
            <span>{displayText}</span>
            {shipmentCount === 1 && hasSpotShipment && (
              <span className='text-[10px] py-2 px-4 font-semibold rounded-6 bg-neutral-10 text-neutral-50'>SPOT</span>
            )}
          </div>
        );
      },
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'date',
      accessorFn: (row) => {
        let date: string;
        switch (dateReference) {
          case 'PICKUP_DATE':
            date = row.pickupDate;
            break;
          case 'DELIVERY_DATE':
            date = row.deliveryDate;
            break;
          case 'INVOICE_DATE':
            date = row.invoiceDate;
            break;
          case 'INVOICE_DUE_DATE':
            date = row.invoiceDueDate;
            break;
          case 'SUBMISSION_DATE':
            date = row.submissionDate;
            break;
          default:
            throw new Error('Invalid date reference');
        }
        return formatDateString(date);
      },
      header: 'DATE',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>DATE</div>
          <div className='text-10 xl:text-12 text-[#808080] font-medium'>{beautifyEnum(dateReference)}</div>
        </div>
      ),
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'serviceProvider',
      accessorFn: (row) => beautifyEnum(row.serviceProvider) || '-',
      header: 'SERVICE PROVIDER',
      size: 150,
    },
    {
      enableEditing: false,
      accessorKey: 'modality',
      accessorFn: (row) => row.modality || '-',
      header: 'MODALITY',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'billedEntity',
      accessorFn: (row) => (row.billedEntity ? beautifyEnum(row.billedEntity).toUpperCase() : '-'),
      header: 'BILLED ENTITY',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'accountNumber',
      accessorFn: (row) => row.accountNumber || '-',
      header: 'ACCOUNT NUMBER',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'totalAmount',
      accessorFn: (row) => `${row?.invoiceCurrency || '-'} ${formatNumber(row?.totalAmount) || '-'}`,
      header: 'Total Amount (Invoice Currency)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>TOTAL AMOUNT</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Invoice Currency</span>
        </div>
      ),
      size: 180,
      minSize: 180,
    },
    {
      enableEditing: false,
      accessorKey: 'totalAmountInReferenceCurrency',
      accessorFn: (row) => {
        const currency = row?.invoiceReferenceCurrency || currentCurrency;
        return `${currency || '-'} ${formatNumber(row?.totalAmountInReferenceCurrency) || '-'}`;
      },
      header: 'Total Amount (Currency Reference)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>TOTAL AMOUNT</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Currency Reference</span>
        </div>
      ),
      size: 180,
      minSize: 180,
    },
    {
      enableEditing: false,
      accessorKey: 'fapRef',
      accessorFn: (row) => row?.fapRef || '-',
      header: 'FAP REF',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'resolutionDate',
      accessorFn: (row: { resolutionDate: string }) => formatDateString(row?.resolutionDate) || '-',
      header: 'RESOLUTION DATE',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'invoiceChargesCount',
      accessorFn: (row) => row.invoiceChargesCount || '-',
      header: 'NO. CHARGES',
      size: 20,
    },
    {
      enableEditing: false,
      accessorFn: (row) => (
        <button type='button' onClick={() => window.open(`/action-details/${row.invoiceId}?hideBack=1`)}>
          <img src={EyeIcon} alt='Detail' />
        </button>
      ),
      header: 'DETAILS',
      enableSorting: false,
      accessorKey: 'actions',
      size: 20,
    },
  ].filter(Boolean);

export const getChargesColumns = ({ dateReference, currentCurrency }: { dateReference: string; currentCurrency?: string }) =>
  [
    {
      enableEditing: false,
      accessorKey: 'chargeDomain',
      accessorFn: (row) => beautifyEnum(row.chargeDomain)?.toUpperCase() || '-',
      header: 'CHARGE DOMAIN',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'chargeName',
      accessorFn: (row) => row.chargeName || '-',
      header: 'CHARGE NAME',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'legType',
      accessorFn: (row) => {
        const legTypeMapping = {
          LEG_ONE: '1',
          LEG_TWO: '2',
          LEG_THREE: '3',
        };
        return legTypeMapping[row.legType] || '-';
      },
      header: 'LEG',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'serviceType',
      accessorFn: (row) => row.service || '-',
      header: 'SERVICE',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'serviceProvider',
      accessorFn: (row) => beautifyEnum(row.serviceProvider) || '-',
      header: 'SERVICE PROVIDER',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'modality',
      accessorFn: (row) => row.modality || '-',
      header: 'MODALITY',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'billedEntity',
      accessorFn: (row) => (row.billedEntity ? beautifyEnum(row.billedEntity).toUpperCase() : '-'),
      header: 'BILLED ENTITY',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'deviationAmountInInvoiceCurrency',
      accessorFn: (row) => `${row?.invoiceCurrency || '-'} ${formatNumber(row?.deviationAmountInInvoiceCurrency) || '-'}`,
      header: 'Deviation Amount (Invoice Currency)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>DEVIATION AMOUNT</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Invoice Currency</span>
        </div>
      ),
      size: 200,
      minSize: 200,
    },
    {
      enableEditing: false,
      accessorKey: 'chargeAmount',
      accessorFn: (row) => `${row?.chargeCurrency || '-'} ${formatNumber(row?.chargeAmount || '-')}`,
      header: 'Charge Amount (Original Currency)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>CHARGE AMOUNT</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Original Currency</span>
        </div>
      ),
      size: 180,
      minSize: 180,
    },
    {
      enableEditing: false,
      accessorKey: 'chargeAmountInInvoiceCurrency',
      accessorFn: (row) => `${row?.invoiceCurrency || '-'} ${formatNumber(row?.chargeAmountInInvoiceCurrency) || '-'}`,
      header: 'Charge Amount (Invoice Currency)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>CHARGE AMOUNT</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Invoice Currency</span>
        </div>
      ),
      size: 180,
      minSize: 180,
    },
    {
      enableEditing: false,
      accessorKey: 'chargeAmountInReferenceCurrency',
      accessorFn: (row) => {
        const currency = row?.referenceCurrency || currentCurrency;
        return `${currency || '-'} ${formatNumber(row?.chargeAmountInReferenceCurrency) || '-'}`;
      },
      header: 'Charge Amount (Currency Reference)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>CHARGE AMOUNT</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Currency Reference</span>
        </div>
      ),
      size: 180,
      minSize: 180,
    },
    {
      enableEditing: false,
      accessorKey: 'chargeQuantity',
      accessorFn: (row) => row.chargeQuantity || '-',
      header: 'CHARGE QUANTITY',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'quantityUnit',
      accessorFn: (row) => row.quantityUnit || '-',
      header: 'QUANTITY UNIT',
      size: 150,
    },
    {
      enableEditing: false,
      accessorKey: 'chargeStatus',
      accessorFn: (row) => row.chargeStatus || '-',
      header: 'CHARGE STATUS',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'chargeStatusReason',
      accessorFn: (row) => row.chargeStatusReason || '-',
      header: 'CHARGE STATUS REASON',
      size: 220,
    },
    {
      enableEditing: false,
      accessorKey: 'chargeUnitPrice',
      accessorFn: (row) => `${row?.chargeCurrency || '-'} ${formatNumber(row?.chargeUnitPrice) || '-'}`,
      header: 'Unit Price (Original Currency)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>UNIT PRICE</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Original Currency</span>
        </div>
      ),
      size: 180,
      minSize: 180,
    },
    {
      enableEditing: false,
      accessorKey: 'exchangeRate',
      accessorFn: (row) => `${row?.chargeCurrency || '-'} 1 = ${row?.invoiceCurrency || '-'} ${formatNumber(row?.exchangeRate) || '-'}`,
      header: 'Exchange Rate (Original Currency to Invoice Currency)',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>EXCHANGE RATE</div>
          <span className='text-10 xl:text-12 text-[#808080] font-medium'>Original Ccy to Invoice Ccy</span>
        </div>
      ),
      size: 220,
      minSize: 220,
    },
    {
      enableEditing: false,
      accessorKey: 'shipmentNumber',
      accessorFn: (row) => row.shipmentNumber || '-',
      header: 'SHIP NO.',
      size: 20,
      Cell: ({ row }) => (
        <div className='flex items-center justify-center gap-8'>
          <span>{row.original.shipmentNumber || '-'}</span>
          {row.original.isSpotShipment && <span className='text-[10px] py-2 px-4 font-semibold rounded-6 bg-neutral-10 text-neutral-50'>SPOT</span>}
        </div>
      ),
    },
    {
      enableEditing: false,
      accessorKey: 'invoiceNumber',
      accessorFn: (row) => row.invoiceNumber || '-',
      header: 'INVOICES NO.',
      size: 20,
    },
    {
      enableEditing: false,
      enableSorting: true,
      accessorKey: 'date',
      accessorFn: (row) => {
        let date: string;
        switch (dateReference) {
          case 'PICKUP_DATE':
            date = row.pickupDate;
            break;
          case 'DELIVERY_DATE':
            date = row.deliveryDate;
            break;
          case 'INVOICE_DATE':
            date = row.invoiceDate;
            break;
          case 'INVOICE_DUE_DATE':
            date = row.invoiceDueDate;
            break;
          case 'SUBMISSION_DATE':
            date = row.submissionDate;
            break;
          default:
            throw new Error('Invalid date reference');
        }
        return formatDateString(date);
      },
      header: 'DATE',
      Header: (
        <div className='text-left leading-16 xl:leading-18'>
          <div>DATE</div>
          <div className='text-10 xl:text-12 text-[#808080] font-medium'>{beautifyEnum(dateReference)}</div>
        </div>
      ),
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'pol',
      accessorFn: (row) => row.pol || '-',
      header: 'POL',
      size: 20,
    },
    {
      enableEditing: false,
      accessorKey: 'pod',
      accessorFn: (row) => row.pod || '-',
      header: 'POD ',
      size: 20,
    },
    {
      enableEditing: false,
      accessorFn: (row) => (
        <button type='button' onClick={() => window.open(`/action-details/${row.invoiceId}?hideBack=1&shipmentNumber=${row.shipmentNumber}`)}>
          <img src={EyeIcon} alt='Detail' />
        </button>
      ),
      header: 'DETAILS',
      enableSorting: false,
      accessorKey: 'actions',
      size: 20,
    },
  ].filter(Boolean);

export enum TableTab {
  SHIPMENTS = 'Shipments',
  INVOICES = 'Invoices',
  CHARGES = 'Charges',
  SAVINGS = 'SAVINGS',
  VOLUMETRIC = 'VOLUMETRIC',
}

export const dateColumnOptions = [
  {
    key: 'pickupDate',
    label: 'Pickup Date',
    value: 'PICKUP_DATE',
  },
  {
    key: 'deliveryDate',
    label: 'Delivery Date',
    value: 'DELIVERY_DATE',
  },
  {
    key: 'invoiceDate',
    label: 'Invoice Date',
    value: 'INVOICE_DATE',
  },
  {
    key: 'invoiceDueDate',
    label: 'Invoice Due Date',
    value: 'INVOICE_DUE_DATE',
  },
  {
    key: 'submissionDate',
    label: 'Submission Date',
    value: 'SUBMISSION_DATE',
  },
];
